import React, { useEffect, useMemo } from "react";
import { ExchangeRateDto, IUserBalanceUpdate, IUserDto } from "interfaces";
import { AppContext, TAppAccessURL } from "./AppContext";
import { useRouter } from "next/router";
import { triggerNotification } from "components/TheComponents/Notification";
import { socket } from "socket";
import {
  EndedRaffleData,
  LeaderboardRaffleStartData,
  Raffle,
  RaffleWithIdData,
} from "types/raffles";
import { LiveStreamData, Stream } from "helpers/types";
import { ADMIN_ROLES, SocketEvent } from "helpers/constants";
import { useDisclosure } from "@chakra-ui/react";
import useCurrentUser, { useUserClientActions } from "hooks/user/useCurrentUser";
import { ExceptionHandlingType } from "types/apiServiceTypes";
import apiService from "services/apiService";
import { useShowPageLayoutContext } from "./ShowPageLayoutContext";
import { NotificationData } from "types/notification";
import useFeatureFlags from "hooks/feature-flag/useFeatureFlags";
import Loader from "components/wrewards-ui-kit/Loader";
import { EMPTY_FLAGS } from "constants/commot";
import { ChallengeT } from "types/challenges";
import { useQueryClient } from "@tanstack/react-query";
import { useActiveChallengesCountsKey } from "hooks/challenges/api/useAnyActiveChallenges";

interface IAppProviderProps {
  children?: React.ReactNode;
}

export const AppProvider: React.FC<IAppProviderProps> = ({ children }): JSX.Element => {
  const router = useRouter();
  const { isReviewed } = useShowPageLayoutContext();
  const queryClient = useQueryClient();

  const { logout } = useUserClientActions();
  const { isOpen: isProfileOpen, onOpen: onOpenProfile, onClose: onCloseProfile } = useDisclosure();
  const { isOpen: isLoginOpen, onOpen: onOpenLogin, onClose: onCloseLogin } = useDisclosure();
  const { data: featureFlags } = useFeatureFlags();

  const [showQrModal, setShowQrModal] = React.useState<boolean>(false);

  useEffect(() => {
    apiService.init(logout);
    return () => {
      socket.off("connect");
      socket.off("disconnect");
    };
  }, [logout]);

  const { data: profile, isFetching: isLoading, refetch } = useCurrentUser();

  const [userBalance, setUserBalance] = React.useState<
    Pick<IUserDto, "gPointsBalance" | "pointsBalance">
  >({});

  const [newRaffle, setNewRaffle] = React.useState<Raffle | null>(null);
  const [endingRaffle, setEndingRaffle] = React.useState<Raffle | null>(null);
  const [liveStreams, setLiveStreams] = React.useState<Stream[]>([]);
  const [rates, setRates] = React.useState<ExchangeRateDto | null>(null);

  const updateBalance = React.useCallback(async (balance: IUserBalanceUpdate) => {
    if (balance?.balanceType === "G_POINT") {
      setUserBalance((oldBalance) => ({
        ...(oldBalance! || {}),
        gPointsBalance: balance.balance,
      }));
    }
    if (balance?.balanceType === "POINTS") {
      setUserBalance((oldBalance) => ({
        ...(oldBalance! || {}),
        pointsBalance: balance.balance,
      }));
    }
  }, []);

  useEffect(() => {
    socket.on(SocketEvent.StreamsStatusesCheck, (data) => {
      const onlineStreams = data?.filter((el: Stream) => el.isLive === true);
      setLiveStreams(onlineStreams);
    });

    socket.on(SocketEvent.StreamStatusNotify, (data: NotificationData<LiveStreamData>) => {
      triggerNotification({
        type: "stream-live",
        streamToastProps: data,
      });
      isReviewed.setValue(false);
    });

    socket.on(SocketEvent.NotifyNewCalendarDay, (data: NotificationData<null>) => {
      triggerNotification({
        type: "calendar-day",
        calendarToastProps: data,
      });
      isReviewed.setValue(false);
    });

    socket.on(SocketEvent.NewRaffle, ({ data, notificationData }: NotificationData<Raffle>) => {
      setNewRaffle(data);
      triggerNotification({
        type: "raffle-start",
        toastId: SocketEvent.NewRaffle,
        raffleStartToastProps: {
          data,
          notificationData,
        },
      });
      isReviewed.setValue(false);
    });

    socket.on(
      SocketEvent.ChallengeStart,
      ({ data, notificationData }: NotificationData<ChallengeT>) => {
        triggerNotification({
          type: "challenge-start",
          toastId: SocketEvent.ChallengeStart,
          challengeToastProps: {
            data,
            notificationData,
          },
        });

        queryClient.invalidateQueries({
          queryKey: [useActiveChallengesCountsKey],
        });

        isReviewed.setValue(false);
      }
    );

    socket.on(
      SocketEvent.ChallengeEndSoon,
      ({ data, notificationData }: NotificationData<ChallengeT>) => {
        triggerNotification({
          type: "challenge-end",
          toastId: SocketEvent.ChallengeEndSoon,
          challengeToastProps: {
            data,
            notificationData,
          },
        });
        isReviewed.setValue(false);
      }
    );

    socket.on(SocketEvent.BalanceUpdate, (data) => {
      updateBalance(data);
    });

    socket.on(
      SocketEvent.RaffleRouletteStarts,
      ({ data, notificationData }: NotificationData<RaffleWithIdData>) => {
        triggerNotification({
          toastId: SocketEvent.RaffleRouletteStarts,
          type: "raffle-start",
          raffleStartToastProps: { data, notificationData },
        });
      }
    );

    socket.on(
      SocketEvent.LeaderboardRaffleRouletteStarts,
      ({ data, notificationData }: NotificationData<LeaderboardRaffleStartData>) => {
        triggerNotification({
          toastId: SocketEvent.LeaderboardRaffleRouletteStarts,
          type: "leaderboard-raffle-start",
          leaderboardRaffleToastProps: { data, notificationData },
        });
      }
    );

    socket.on(SocketEvent.NotifyNewNotification, () => {
      isReviewed.setValue(false);
    });

    socket.on(
      SocketEvent.RaffleEndsSoon,
      ({ data, notificationData }: NotificationData<EndedRaffleData>) => {
        setEndingRaffle(data as any);
        triggerNotification({
          type: "raffle-end",
          raffleEndToastProps: { data, notificationData },
        });
      }
    );

    socket.on(
      SocketEvent.ChallengeWin,
      ({ data, notificationData }: NotificationData<ChallengeT>) => {
        triggerNotification({
          type: "challenge-win",
          toastId: SocketEvent.ChallengeWin,
          challengeToastProps: {
            data,
            notificationData,
          },
        });
        isReviewed.setValue(false);
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateBalance]);

  const isAdmin = useMemo(() => {
    return profile?.roles?.some((userRole) =>
      ADMIN_ROLES.some((adminRole) => adminRole.name === userRole.name)
    );
  }, [profile?.roles]);

  // TODO globally removing step by step
  // Using useCallback to memoize accessFetch and accessURL
  const accessFetch = React.useCallback(
    (
      input: string | URL,
      init?: RequestInit,
      exceptionHandlingType: ExceptionHandlingType = ExceptionHandlingType.MANUAL
    ) => apiService.request(input, init, exceptionHandlingType),
    []
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const accessURL = React.useCallback(appAccessURL(), []);

  React.useEffect(() => {
    const getStreamsStatuses = async () => {
      const streamsResponse = await accessFetch(
        `/stats/streams-statuses`,
        undefined,
        ExceptionHandlingType.SILENT
      );
      const streamsData = await streamsResponse?.json();
      const onlineStreams = streamsData?.filter((el: Stream) => el.isLive === true);
      setLiveStreams(onlineStreams);
    };

    getStreamsStatuses();
  }, [accessFetch]);

  const onDiscordRedirect = React.useCallback(async () => {
    try {
      const url = accessURL(`/auth/discord`) as URL;
      router.push(url.href);
    } catch (error) {
      console.error({ error });
    }
  }, [accessURL, router]);

  const onSteamRedirect = React.useCallback(async () => {
    try {
      const url = accessURL(`/auth/steam`) as URL;
      router.push(url.href);
    } catch (error) {
      console.error({ error });
    }
  }, [accessURL, router]);

  // Fetch and subscribe on exchange rate
  const fetchRate = React.useCallback(async () => {
    const url = accessURL(`/balance/rates`) as URL;
    const response = await accessFetch(url.href, undefined, ExceptionHandlingType.SILENT);
    const data = await response?.json();
    setRates(data as ExchangeRateDto);
  }, [accessFetch, accessURL]);

  useEffect(() => {
    fetchRate();
    socket.on(SocketEvent.RatesUpdate, setRates);
  }, [fetchRate]);

  const isFeatureEnabled = (code: string) => {
    return featureFlags?.find?.((flag) => flag.code === code)?.enabled ?? false;
  };

  const featureFlagsSafe = featureFlags ?? EMPTY_FLAGS; //featureFlags or the same array to not ruin memoisation

  const contextValue = useMemo(
    () => ({
      apiService,
      accessFetch,
      accessURL,
      showQrModal,
      setShowQrModal,
      profile: profile ? { ...profile, ...userBalance } : null,
      featureFlags: featureFlagsSafe,
      setRates,
      rates,
      isAdmin,
      liveStreams,
      newRaffle,
      profileProps: { isProfileOpen, onOpenProfile, onCloseProfile },
      loginModalProps: { isLoginOpen, onOpenLogin, onCloseLogin },
      endingRaffle,
      onDiscordRedirect,
      onSteamRedirect,
      isFeatureEnabled,
      isProfileLoading: isLoading,
      refetchProfile: refetch,
      setUserBalance,
    }),
    [
      accessFetch,
      accessURL,
      showQrModal,
      profile,
      userBalance,
      featureFlags,
      rates,
      isAdmin,
      liveStreams,
      newRaffle,
      isProfileOpen,
      onOpenProfile,
      onCloseProfile,
      isLoginOpen,
      onOpenLogin,
      onCloseLogin,
      endingRaffle,
      onDiscordRedirect,
      onSteamRedirect,
      isFeatureEnabled,
      isLoading,
      refetch,
    ]
  );

  if (!featureFlags)
    return (
      <div style={{ height: "100vh", display: "flex" }}>
        <Loader />
      </div>
    );

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export function useAppContext() {
  const context = React.useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within a AppProvider");
  }
  return context;
}

/**
 * Create accessURL binded to domain (from env.NEXT_PUBLIC_APP_BE_ORIGIN)
 * if not one is present in input value
 *
 * @returns TAppAccessURL
 *
 */
function appAccessURL(): TAppAccessURL {
  const urlOrigin: string = process.env.NEXT_PUBLIC_APP_BE_ORIGIN ?? "http://localhost:4000";
  return (input: string, origin: string = urlOrigin): URL => {
    const url = new URL(input, origin);
    return url;
  };
}
